import { useState } from "react";
import { Button, List, Modal, Nav, Panel, Placeholder, SelectPicker, Stack, Tag } from "rsuite";
import styles from "../styles/importPage.module.scss";
import { ImportMethodsUtils } from "../../../core/utils/ImportMethodsUtils";
import { formatToBRLCurrency } from "../../../utilities/formatToBRLCurrency";

interface LogsModalProps {
    open: boolean;
    data: any | null;
    onClose: () => void;
    integrationsSelect: { value: number, label: string }[];
}

export default function LogsModal({ open, data, onClose, integrationsSelect }: LogsModalProps) {
    const [entityTab, setEntityTab] = useState<"clients" | "accounts">("accounts");
    const [statusTab, setStatusTab] = useState<"ok" | "err">("ok");

    async function handleExportExcelAll() {
        if (!data) return;
        const XLSX = await import("xlsx");

        const clientsOkRows = (data.clients?.ok ?? []).map((c: any) => ({
            Linha: c.rowInSheet ?? "",
            Nome: c.name ?? "",
            "CPF/CNPJ": c.cpfcnpj ?? "",
            Tipo: c.type ?? "",
            Email: c.email ?? "",
            "Nome Fantasia": c.fantasyName ?? "",
        }));
        const clientsErrRows = (data.clients?.errors ?? []).map((e: any) => ({
            Linha: e.rowInSheet ?? "",
            Nome: e.item?.name ?? "",
            "CPF/CNPJ": e.item?.cpfcnpj ?? "",
            Erros: ImportMethodsUtils.summarizeErrors(e.errors ?? []),
        }));
        const accountsOkRows = (data.accounts?.ok ?? []).map((a: any) => ({
            Linha: a.rowInSheet ?? "",
            "CPF/CNPJ": a.cpfcnpj ?? "",
            Tipo: a.type ?? "",
            Valor: a.valueToBePaid ?? a.rawValue ?? "",
            "Nº Documento": a.documentNumber ?? "",
        }));
        const accountsErrRows = (data.accounts?.errors ?? []).map((e: any) => ({
            Linha: e.rowInSheet ?? "",
            "CPF/CNPJ": e.item?.cpfcnpj ?? "",
            Tipo: e.item?.type ?? "",
            Erros: ImportMethodsUtils.summarizeErrors(e.errors ?? []),
        }));

        const mkSheet = (headers: string[], rows: Record<string, any>[], widths: Array<{ wch: number }>) => {
            const aoa = [headers, ...rows.map(r => headers.map(h => r[h] ?? ""))];
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws["!cols"] = widths;
            const range = { s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: headers.length - 1 } };
            ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
            ws["!rows"] = [{ hpt: 22 }];
            (ws as any)["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };
            return ws;
        };

        const wb = XLSX.utils.book_new();

        {
            const headers = Object.keys(clientsOkRows[0] ?? { Linha: "", Nome: "", "CPF/CNPJ": "", Tipo: "", Email: "", "Nome Fantasia": "" });
            const ws = mkSheet(headers, clientsOkRows, [{ wch: 8 }, { wch: 40 }, { wch: 18 }, { wch: 10 }, { wch: 28 }, { wch: 28 }]);
            XLSX.utils.book_append_sheet(wb, ws, "Clientes - Sucesso");
        }

        {
            const headers = Object.keys(clientsErrRows[0] ?? { Linha: "", Nome: "", "CPF/CNPJ": "", Erros: "" });
            const ws = mkSheet(headers, clientsErrRows, [{ wch: 8 }, { wch: 40 }, { wch: 18 }, { wch: 80 }]);
            XLSX.utils.book_append_sheet(wb, ws, "Clientes - Erros");
        }

        {
            const headers = Object.keys(accountsOkRows[0] ?? { Linha: "", "CPF/CNPJ": "", Tipo: "", Valor: "", "Nº Documento": "" });
            const ws = mkSheet(headers, accountsOkRows, [{ wch: 8 }, { wch: 18 }, { wch: 10 }, { wch: 16 }, { wch: 18 }]);
            XLSX.utils.book_append_sheet(wb, ws, "Contas - Sucesso");
        }

        {
            const headers = Object.keys(accountsErrRows[0] ?? { Linha: "", "CPF/CNPJ": "", Tipo: "", Erros: "" });
            const ws = mkSheet(headers, accountsErrRows, [{ wch: 8 }, { wch: 18 }, { wch: 10 }, { wch: 80 }]);
            XLSX.utils.book_append_sheet(wb, ws, "Contas - Erros");
        }

        XLSX.writeFile(wb, `preview-todas-as-abas-${Date.now()}.xlsx`);
    }

    return (
        <Modal open={open} onClose={onClose} size="lg">
            <Modal.Header className={styles.modalHeader}>
                <Modal.Title>Pré visualização da importação</Modal.Title>
                <SelectPicker data={integrationsSelect} value={data?.integrationId} disabled />
            </Modal.Header>

            <Modal.Body>
                {data ? (
                    <>
                        <Nav
                            appearance="tabs"
                            activeKey={entityTab}
                            onSelect={(key) => setEntityTab(key as "clients" | "accounts")}
                            className="mb-2"
                        >
                            <Nav.Item eventKey="clients">
                                {`Clientes / Fornecedores (${data.summary.clientsOk} ok / ${data.summary.clientsErr} erros)`}
                            </Nav.Item>
                            <Nav.Item eventKey="accounts">
                                {`Contas (${data.summary.accountsOk} ok / ${data.summary.accountsErr} erros)`}
                            </Nav.Item>
                        </Nav>

                        <Nav
                            appearance="subtle"
                            activeKey={statusTab}
                            onSelect={(key) => setStatusTab(key as "ok" | "err")}
                            className="mb-3"
                        >
                            <Nav.Item eventKey="ok">
                                {entityTab === "clients" ? `Sucesso (${data.clients.ok.length})` : `Sucesso (${data.accounts.ok.length})`}
                            </Nav.Item>
                            <Nav.Item eventKey="err">
                                {entityTab === "clients" ? `Erros (${data.clients.errors.length})` : `Erros (${data.accounts.errors.length})`}
                            </Nav.Item>
                        </Nav>

                        <Panel bordered bodyFill className={styles.resultPanel}>
                            {entityTab === "clients" && statusTab === "ok" && (
                                <>
                                    {data.clients.ok.length === 0 ? (
                                        <Placeholder.Paragraph rows={1} />
                                    ) : (
                                        <List bordered>
                                            {data.clients.ok.map((c: any) => (
                                                <List.Item key={`cok-${c.rowInSheet}`} className={styles.resultItemOk}>
                                                    <Stack justifyContent="space-between" alignItems="center">
                                                        <span>
                                                            <strong>Linha {c.rowInSheet}</strong> — {c.name} ({c.cpfcnpj})
                                                        </span>
                                                        <Tag color="green">OK</Tag>
                                                    </Stack>
                                                    <div className={styles.resultMsg}>
                                                        {c.type}
                                                        {c.email ? ` • ${c.email}` : ""}
                                                        {c.fantasyName ? ` • ${c.fantasyName}` : ""}
                                                    </div>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}

                            {entityTab === "clients" && statusTab === "err" && (
                                <>
                                    {data.clients.errors.length === 0 ? (
                                        <Placeholder.Paragraph rows={1} />
                                    ) : (
                                        <List bordered>
                                            {data.clients.errors.map((e: any) => (
                                                <List.Item key={`cerr-${e.rowInSheet}`} className={styles.resultItemErr}>
                                                    <Stack justifyContent="space-between" alignItems="center">
                                                        <span>
                                                            <strong>Linha {e.rowInSheet}</strong> — {e.item.name || "(sem nome)"}
                                                            {e.item.cpfcnpj ? ` (${e.item.cpfcnpj})` : ""}
                                                        </span>
                                                        <Tag color="red">Erro</Tag>
                                                    </Stack>
                                                    <div className={styles.resultMsg}>
                                                        {ImportMethodsUtils.summarizeErrors(e.errors)}
                                                    </div>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}

                            {entityTab === "accounts" && statusTab === "ok" && (
                                <>
                                    {data.accounts.ok.length === 0 ? (
                                        <Placeholder.Paragraph rows={1} />
                                    ) : (
                                        <List bordered>
                                            {data.accounts.ok.map((a: any) => (
                                                <List.Item key={`aok-${a.rowInSheet}`} className={styles.resultItemOk}>
                                                    <Stack justifyContent="space-between" alignItems="center">
                                                        <span>
                                                            <strong>Linha {a.rowInSheet}</strong> — {a.cpfcnpj} • {a.type}
                                                        </span>
                                                        <Tag color="green">OK</Tag>
                                                    </Stack>
                                                    <div className={styles.resultMsg}>
                                                        {` • Valor R$ ${formatToBRLCurrency(String(a.rawValue)) || "-"}`}
                                                        {a.documentNumber ? ` • Doc ${a.documentNumber}` : ""}
                                                    </div>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}

                            {entityTab === "accounts" && statusTab === "err" && (
                                <>
                                    {data.accounts.errors.length === 0 ? (
                                        <Placeholder.Paragraph rows={1} />
                                    ) : (
                                        <List bordered>
                                            {data.accounts.errors.map((e: any) => (
                                                <List.Item key={`aerr-${e.rowInSheet}`} className={styles.resultItemErr}>
                                                    <Stack justifyContent="space-between" alignItems="center">
                                                        <span>
                                                            <strong>Linha {e.rowInSheet}</strong> — {e.item.cpfcnpj || "(sem cpf/cnpj)"} {e.item.type ? ` • ${e.item.type}` : ""}
                                                        </span>
                                                        <Tag color="red">Erro</Tag>
                                                    </Stack>
                                                    <div className={styles.resultMsg}>
                                                        {ImportMethodsUtils.summarizeErrors(e.errors)}
                                                    </div>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}
                        </Panel>
                    </>
                ) : (
                    <Placeholder.Paragraph rows={4} />
                )}
            </Modal.Body>

            <Modal.Footer>
                <Stack spacing={8} className={styles.resultModalFooter}>
                    <Button appearance="primary" onClick={handleExportExcelAll} disabled={!data}>
                        Baixar Excel
                    </Button>
                    <Button appearance="subtle" onClick={onClose}>
                        Fechar
                    </Button>
                </Stack>
            </Modal.Footer>
        </Modal>
    );
}
